export type PositionType = 'token' | 'futures' | 'options';

// Options instruments always end "-<strike>-C" or "-<strike>-P" (Deribit:
// "BTC-24JUL26-64000-C"; OKX: "BTC-USD-260731-66000-C" — same suffix
// convention, extra quote-currency segment). Everything else with a dash
// is a futures/perpetual contract ("BTC-PERPETUAL", "BTC-USD-SWAP", dated
// futures); a bare symbol with no dash at all ("BTC", "ETH") is the
// account's spot/wallet balance.
const OPTION_SUFFIX_RE = /-(C|P)$/;

export const classifyInstrument = (instrument: string): PositionType => {
  if (!instrument.includes('-')) return 'token';
  if (OPTION_SUFFIX_RE.test(instrument)) return 'options';
  return 'futures';
};

const MONTH_INDEX: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

const MONTH_NAMES = Object.keys(MONTH_INDEX).sort((a, b) => MONTH_INDEX[a] - MONTH_INDEX[b]);

// Deribit spells expiries "DDMMMYY" (e.g. "24JUL26"); OKX spells the same
// date numerically as "YYMMDD" (e.g. "260731", i.e. 2026-07-31) — convert
// OKX's format to Deribit's so both venues group/sort/display under one
// consistent label downstream. Anything matching neither shape (shouldn't
// happen, but keeps this a total function) passes through unchanged.
const normalizeExpiryLabel = (expiry: string): string => {
  if (/^\d{2}[A-Z]{3}\d{2}$/.test(expiry)) return expiry;

  const match = expiry.match(/^(\d{2})(\d{2})(\d{2})$/);
  if (!match) return expiry;
  const [, yy, mm, dd] = match;
  const monthName = MONTH_NAMES[Number(mm) - 1];
  return monthName ? `${dd}${monthName}${yy}` : expiry;
};

// Options instrument shape is `${BASE}[-${QUOTE}]-${EXPIRY}-${STRIKE}-${C|P}`
// — the expiry is always the segment 3 places from the end, regardless of
// how many venue-specific segments precede it (Deribit has none extra,
// OKX has a quote-currency segment). Returns null for any instrument that
// doesn't have enough segments (grouped under "Other" by the caller
// rather than thrown away).
export const parseOptionExpiry = (instrument: string): string | null => {
  const parts = instrument.split('-');
  return parts.length >= 4 ? normalizeExpiryLabel(parts[parts.length - 3]) : null;
};

// Strike is the segment 2 places from the end (between expiry and the C/P
// suffix), same positional convention as parseOptionExpiry. Returned as the
// raw string ("64000") — callers format it. Null when there aren't enough
// segments to be an option.
export const parseOptionStrike = (instrument: string): string | null => {
  const parts = instrument.split('-');
  return parts.length >= 4 ? parts[parts.length - 2] : null;
};

// Expiry strings ("24JUL26") don't sort correctly as plain text across month
// boundaries (e.g. "05AUG26" < "31JUL26" alphabetically, backwards
// chronologically) — this gives a real sortable timestamp instead.
// Unparseable expiries (the "Other" bucket) sort last.
export const expirySortKey = (expiry: string): number => {
  const match = expiry.match(/^(\d{2})([A-Z]{3})(\d{2})$/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  const [, day, mon, year] = match;
  const month = MONTH_INDEX[mon];
  if (month === undefined) return Number.MAX_SAFE_INTEGER;
  return Date.UTC(2000 + Number(year), month, Number(day));
};
